(() => {
  if (customElements.get('product-media')) {
    return;
  }

  class ProductMedia extends HTMLElement {
    constructor() {
      super();

      this.mainSliderElement = this.querySelector('[data-main-product-slider]');
      this.mainSliderDrag = Number(this.mainSliderElement.dataset.slidesCount) !== 1;
      this.autoHeight = this.hasAttribute('data-autoheight');
      this.thumbsBelow = this.hasAttribute('data-thumbs-below');
      this.mobileThumbnails = this.hasAttribute('data-mobile-thumbnails');
      this.mobileThumbnailSize = Number(this.dataset.mobileThumbnailSize) || 64;
      this.mainSlider = {
        element: this.mainSliderElement,
        bar: this.querySelector('[data-main-product-slider-progress-bar]'),
        instance: null,
        options: {
          pagination: false,
          rewind: true,
          waitForTransition: true,
          flickMaxPages: 0.5,
          arrows: false,
          drag: this.mainSliderDrag,
          speed: 300,
          easing: 'ease-in-out'
        }
      };
      this.thumbsSlider = {
        element: this.querySelector('[data-main-product-thumbs]'),
        nextArrow: this.querySelector('[data-thumbs-next]'),
        instance: null,
        options: this.getThumbOptions()
      }
      this.controls = this.querySelectorAll('[data-control]');

      this.controls.forEach((control) => {
        control.addEventListener('click', this.onControlClick.bind(this));
      });

      if (this.thumbsSlider.nextArrow) {
        this.thumbsSlider.nextArrow.addEventListener('click', (event) => {
          this.onControlClick(event, this.thumbsSlider.instance);
        })
      }

      window.addEventListener('DOMContentLoaded', this.initSlider.bind(this));
      window.addEventListener('resize', debounce(this.initSlider.bind(this), 500));
    }

    getThumbOptions() {
      const useHorizontalThumbnails = this.thumbsBelow || (this.mobileThumbnails && window.innerWidth < 990);
      const mobileThumbnailsPerPage = Math.max(2, Math.floor((window.innerWidth - 48) / (this.mobileThumbnailSize + 8)));

      return {
        pagination: false,
        rewind: true,
        waitForTransition: true,
        flickMaxPages: 0.5,
        arrows: false,
        isNavigation: true,
        direction: useHorizontalThumbnails ? 'ltr' : 'ttb',
        fixedWidth: useHorizontalThumbnails ? (this.mobileThumbnails && !this.thumbsBelow ? `${this.mobileThumbnailSize}px` : '7.2rem') : '9.6rem',
        fixedHeight: useHorizontalThumbnails ? (this.mobileThumbnails && !this.thumbsBelow ? `${this.mobileThumbnailSize}px` : '9.2rem') : undefined,
        height: useHorizontalThumbnails ? undefined : '55.1rem',
        autoHeight: true,
        perPage: useHorizontalThumbnails ? (this.mobileThumbnails && !this.thumbsBelow ? mobileThumbnailsPerPage : 6) : 4,
        gap: useHorizontalThumbnails ? (this.mobileThumbnails && !this.thumbsBelow ? '8px' : '1rem') : '1.6rem',
        speed: 300,
        easing: 'ease-in-out'
      };
    }

    connectedCallback() {
      this.initSlider();

      document.body.addEventListener('touchstart', () => {
        this.querySelectorAll('deferred-media[data-autoplay]').forEach((media) => {
          const iframe = media.querySelector('iframe');

          if (!iframe) return;

          if (iframe.classList.contains('js-youtube')) {
            iframe.contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*');
          }

          if (iframe.classList.contains('js-vimeo')) {
            iframe.contentWindow.postMessage('{"method":"play"}', '*');
          }
        })
      }, { once: true })
    }

    onControlClick(event, sliderInstance = this.mainSlider.instance) {
      event.preventDefault();

      if (!sliderInstance) {
        return;
      }

      const action = event.target.hasAttribute('data-prev') ? '<' : '>';

      sliderInstance.go(action);
    }

    initSlider() {
      if (window.innerWidth >= 990 && !this.mainSlider.element.hasAttribute('data-main-slider-desktop')) {
        this.mainSlider.element.classList.add('is-initialized');

        if (this.mainSlider.instance) {
          this.mainSlider.instance.destroy(true);
          this.mainSlider.instance = null;
        }

        if (this.thumbsSlider.instance) {
          this.thumbsSlider.instance.destroy(true);
          this.thumbsSlider.instance = null;
        }

        return;
      }

      if (this.mainSlider.instance) {
        this.alignMobileThumbnail();
        return;
      }

      if (this.thumbsSlider.element) {
        this.thumbsSlider.options = this.getThumbOptions();
        this.thumbsSlider.instance = new Splide(
          this.thumbsSlider.element,
          this.thumbsSlider.options
        ).mount();
      }

      this.mainSlider.instance = new Splide(
        this.mainSlider.element,
        this.mainSlider.options
      )

      if (this.autoHeight) {
        this.mainSlider.instance.on('mounted', () => {
          this.changeListHeight(
            this.mainSlider.instance.Components.Elements.root,
            this.mainSlider.instance.Components.Elements.slides
          );
        });

        this.mainSlider.instance.on('moved', () => {
          this.changeListHeight(
            this.mainSlider.instance.Components.Elements.root,
            this.mainSlider.instance.Components.Elements.slides
          );
        })
      }

      if (this.mainSlider.bar) {
        this.mainSlider.instance.on('mounted move', () => {
          const sliderEnd = this.mainSlider.instance.Components.Controller.getEnd() + 1;
          this.mainSlider.bar.style.width = `${100 * ( this.mainSlider.instance.index + 1 ) / sliderEnd}%`;
        })
      }

      this.thumbsSlider.instance
        ? this.mainSlider.instance.sync(this.thumbsSlider.instance).mount()
        : this.mainSlider.instance.mount();

      if (this.mobileThumbnails && this.thumbsSlider.instance) {
        this.mainSlider.instance.on('move moved', () => {
          this.alignMobileThumbnail();
        });

        this.alignMobileThumbnail();
      }
    }

    alignMobileThumbnail() {
      if (
        !this.mobileThumbnails ||
        window.innerWidth >= 750 ||
        !this.mainSlider.instance ||
        !this.thumbsSlider.instance
      ) {
        return;
      }

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const track = this.thumbsSlider.element.querySelector('.splide__track');
          const list = this.thumbsSlider.element.querySelector('.splide__list');
          const slides = Array.from(list?.children || []).filter((slide) => (
            !slide.classList.contains('splide__slide--clone')
          ));
          const selected = slides[this.mainSlider.instance.index] || list?.querySelector('.is-active');

          if (!track || !list || !selected) {
            return;
          }

          const gutter = parseFloat(
            window.getComputedStyle(this).getPropertyValue('--jh-pdp-thumbnail-left-gutter-mobile')
          ) || 0;
          const last = slides[slides.length - 1];
          const desiredPosition = gutter - selected.offsetLeft;
          const endPosition = Math.min(
            0,
            track.clientWidth - gutter - (last.offsetLeft + last.offsetWidth)
          );
          const boundedPosition = Math.max(endPosition, Math.min(0, desiredPosition));

          this.thumbsSlider.instance.Components.Move.translate(boundedPosition);
        });
      });
    }

    changeListHeight(rootEl, slides) {
      rootEl.querySelector('.splide__list').style.height =
        `${slides.find((slide) => slide.classList.contains('is-active')).offsetHeight}px`;
    }

    goToSlide(mediaId) {
      if (!this.mainSlider.instance) {
        const media = this.mainSlider.element.querySelector(`[data-media-id="${mediaId}"]`)

        if (!media) {
          return;
        }

        media.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        })

        return;
      }

      const slideIndex = this.mainSlider.instance.s.Elements.slides.findIndex((slide) => (
        Number(slide.dataset.mediaId) === mediaId
      ));

      this.mainSlider.instance.go(slideIndex);
    }
  }

  customElements.define('product-media', ProductMedia);
})();
